import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle, CheckCircle, XCircle, Lightbulb, Tag } from 'lucide-react';
import { getMissedQuestionsRequest, getTopMissedQuestionsRequest } from '../../../modules/students-modules/questions/Actions';
import { openExamHintkModel } from '../../../modules/admin-modules/models/Actions';
import ExamHint from '../../../containers/student/exams/exam-hint';

const MissedQuestions = () => {
    const [activeTab, setActiveTab] = useState('top');
    const [currentHintData, setCurrentHintData] = useState({ videoId: null, model_answer_image: null });
    const [selectedTags, setSelectedTags] = useState([]);
    const dispatch = useDispatch();
    const topMissed = useSelector((state) => state.questions.top_missed);
    const myMissed = useSelector((state) => state.questions.missed);

    useEffect(() => {
        dispatch(getMissedQuestionsRequest({ type: 1 }));
        dispatch(getTopMissedQuestionsRequest())
    }, []);

    // Collect all unique tags from questions
    const allTags = useMemo(() => {
        const tagsMap = new Map();
        
        // Collect tags from top missed questions
        topMissed.forEach((questionData) => {
            const tags = questionData.tags || questionData.question?.tags || [];
            tags.forEach((tag) => {
                if (tag && tag.id && !tagsMap.has(tag.id)) {
                    tagsMap.set(tag.id, tag);
                }
            });
        });
        
        // Collect tags from my missed questions
        myMissed.forEach((questionData) => {
            const tags = questionData.tags || questionData.question?.tags || [];
            tags.forEach((tag) => {
                if (tag && tag.id && !tagsMap.has(tag.id)) {
                    tagsMap.set(tag.id, tag);
                }
            });
        });
        
        return Array.from(tagsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [topMissed, myMissed]);

    // Filter questions based on selected tags
    const filteredTopMissed = useMemo(() => {
        if (selectedTags.length === 0) return topMissed;
        
        return topMissed.filter((questionData) => {
            const tags = questionData.tags || questionData.question?.tags || [];
            const tagIds = tags.map(tag => tag.id);
            return selectedTags.some(selectedTagId => tagIds.includes(selectedTagId));
        });
    }, [topMissed, selectedTags]);

    const filteredMyMissed = useMemo(() => {
        if (selectedTags.length === 0) return myMissed;
        
        return myMissed.filter((questionData) => {
            const tags = questionData.tags || questionData.question?.tags || [];
            const tagIds = tags.map(tag => tag.id);
            return selectedTags.some(selectedTagId => tagIds.includes(selectedTagId));
        });
    }, [myMissed, selectedTags]);

    const handleTagToggle = (tagId) => {
        setSelectedTags(prev => 
            prev.includes(tagId) 
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    };

    const handleOpenHint = (questionData) => {
        const { question } = questionData;
        let videoId = null;
        
        // Extract video ID from URL if it exists
        if (question?.url) {
            const videoData = question.url.split("/");
            const splitLength = videoData.length;
            videoId = videoData[splitLength - 1];
        }

        setCurrentHintData({
            videoId: videoId,
            model_answer_image: question?.model_answer_image
        });
        
        dispatch(openExamHintkModel());
    };

    const renderQuestion = (questionData, index) => {
        const { question, answers, student_answer_id, chapter, wrong_count } = questionData;
        // Get tags from either root level or question object
        const tags = questionData.tags || question?.tags || [];

        return (
            <div key={`${question.id || 'noid'}-${index}`} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-start space-x-4 mb-4">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-600 font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                                <div className="flex items-center flex-wrap gap-2 mb-2">
                                    {/* Chapter tag */}
                                    {chapter && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                            {chapter.name}
                                        </span>
                                    )}

                                    {wrong_count > 0 && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                        {wrong_count} Students missed this question
                                    </span>
                                    )}
                                    {/* Question tags */}
                                    {tags && tags.length > 0 && tags.map((tag) => (
                                        <span
                                            key={tag.id}
                                            className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                                        >
                                            <Tag className="w-3 h-3" />
                                            <span>{tag.name}</span>
                                        </span>
                                    ))}
                                </div>
                                <div
                                    className="text-gray-900 font-medium"
                                    dangerouslySetInnerHTML={{ __html: question.question }}
                                />
                            </div>
                            {/* Hint button - only show if question has hint data */}
                            {(question?.model_answer_image || question?.url) && (
                                <button
                                    onClick={() => handleOpenHint(questionData)}
                                    className="ml-4 flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors duration-200 flex-shrink-0"
                                >
                                    <Lightbulb className="w-4 h-4" />
                                    <span className="text-sm font-medium">Hint</span>
                                </button>
                            )}
                        </div>

                        {question.image && (
                            <div className="mb-4">
                                <img
                                    src={`https://asquera.com/asquera/${question?.image}`}
                                    alt="Question"
                                    className="max-w-50 h-auto max-h-48 rounded-lg border border-gray-200"
                                />
                            </div>
                        )}

                        <div className="space-y-3">
                            {answers.map((answer, answerIndex) => {
                                const isCorrect = answer?.right_answer === 1;
                                // Only check for student answer if student_answer_id is not null
                                const isStudentAnswer = student_answer_id && answer?.id && student_answer_id === answer?.id;
                                const isWrongStudentAnswer = isStudentAnswer && !isCorrect;

                                return (
                                    <div
                                        key={answer.id}
                                        className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all ${isCorrect
                                            ? 'border-green-200 bg-green-50'
                                            : isWrongStudentAnswer
                                                ? 'border-red-200 bg-red-50'
                                                : 'border-gray-200 bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span className="w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-sm font-medium">
                                                {String.fromCharCode(97 + answerIndex)}
                                            </span>
                                            {isCorrect && (
                                                <CheckCircle className="w-5 h-5 text-green-600" />
                                            )}
                                            {isWrongStudentAnswer && (
                                                <XCircle className="w-5 h-5 text-red-600" />
                                            )}
                                        </div>
                                        <span className={`flex-1 ${isCorrect ? 'text-green-800 font-medium' :
                                            isWrongStudentAnswer ? 'text-red-800' : 'text-gray-700'
                                            }`}>
                                            {answer?.answer}
                                        </span>
                                        {/* Only show 'Your answer' if student_answer_id is present */}
                                        {isStudentAnswer && (
                                            <span className={`text-xs px-2 py-1 rounded-full ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                }`}>
                                                Your answer
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="border-b border-gray-200">
                <div className="flex items-center space-x-3 p-6 pb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Missed Questions</h3>
                </div>

                <div className="flex space-x-1 px-6">
                    <button
                        onClick={() => setActiveTab('top')}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'top'
                            ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-600'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Top Missed Questions
                    </button>
                    <button
                        onClick={() => setActiveTab('my')}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'my'
                            ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-600'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        My Missed Questions
                    </button>
                </div>
            </div>

            <div className="p-6">
                {/* Tag Filter Section */}
                {allTags.length > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center space-x-2 mb-3">
                            <Tag className="w-4 h-4 text-gray-600" />
                            <h4 className="text-sm font-semibold text-gray-700">Filter by Tags:</h4>
                            {selectedTags.length > 0 && (
                                <button
                                    onClick={() => setSelectedTags([])}
                                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {allTags.map((tag) => {
                                const isSelected = selectedTags.includes(tag.id);
                                return (
                                    <button
                                        key={tag.id}
                                        onClick={() => handleTagToggle(tag.id)}
                                        className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                            isSelected
                                                ? 'bg-orange-500 text-white border border-orange-600'
                                                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                                        }`}
                                    >
                                        <Tag className="w-3 h-3" />
                                        <span>{tag.name}</span>
                                        {isSelected && (
                                            <span className="ml-1 text-xs">✓</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {selectedTags.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                                Showing {activeTab === 'top' ? filteredTopMissed.length : filteredMyMissed.length} question(s) with selected tags
                            </p>
                        )}
                    </div>
                )}

                {activeTab === 'top' && (
                    <div>
                        <p className="text-gray-600 mb-6">
                            Questions that are commonly missed by students in your class
                        </p>
                        <div className="h-96 overflow-y-auto pr-2">
                            {filteredTopMissed.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No questions found with the selected tags.</p>
                                </div>
                            ) : (
                                filteredTopMissed.map((questionData, index) =>
                                    renderQuestion(questionData, index)
                                )
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'my' && (
                    <div>
                        <p className="text-gray-600 mb-6">
                            Questions you have answered incorrectly
                        </p>
                        <div className="h-96 overflow-y-auto pr-2">
                            {filteredMyMissed.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No questions found with the selected tags.</p>
                                </div>
                            ) : (
                                filteredMyMissed.map((questionData, index) =>
                                    renderQuestion(questionData, index)
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Exam Hint Modal */}
            <ExamHint
                videoId={currentHintData.videoId}
                model_answer_image={currentHintData.model_answer_image}
            />
        </div>
    );
};

export default MissedQuestions;